import React, { useState } from "react";
import { PenLine, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";

export default function CrewSignoff({ cid, roles, session, defaultName, suggestions, onSaved }) {
  const roleList = roles && roles.length ? roles : ["Employee"];
  const [role, setRole] = useState(roleList[0]);
  const [empName, setEmpName] = useState(defaultName || "");
  const [signature, setSignature] = useState("");
  const [timeOut, setTimeOut] = useState("");
  const [busy, setBusy] = useState(false);

  const nowTime = () => setTimeOut(new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }));

  const submit = async () => {
    if (!empName.trim() || !signature.trim() || !timeOut.trim()) {
      return toast.error("Fill in name, signature, and time out");
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/checklists/${cid}/signoff`, {
        role, employee_name: empName, signature, time_out: timeOut,
      });
      toast.success("Sign-off recorded");
      setSignature("");
      onSaved?.(data.session);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const signoffs = session?.signoffs || [];

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-[#2E2E2E] bg-[#171717] p-5">
        <div className="flex items-center gap-2 mb-4">
          <PenLine className="h-5 w-5 text-[#E11D2A]" />
          <h3 className="font-heading text-lg font-semibold">Crew Accountability Sign-Off</h3>
        </div>
        <p className="text-sm text-[#A3A3A3] mb-4">
          By signing, you certify your assigned tasks were fully completed and inspected to Paige's Bistro standards.
        </p>

        <label className="font-mono text-[10px] uppercase tracking-widest text-[#6B6B6B]">Station / Role</label>
        <div className="mt-1.5 grid grid-cols-2 gap-2 mb-4">
          {roleList.map((s) => (
            <button
              key={s}
              onClick={() => setRole(s)}
              className={`rounded-xl border px-3 py-2.5 text-sm transition-colors ${role === s ? "bg-[#E11D2A] border-[#E11D2A] text-white font-semibold" : "bg-[#0E0E0E] border-[#2E2E2E] text-[#A3A3A3]"}`}
            >
              {s}
            </button>
          ))}
        </div>

        <label className="font-mono text-[10px] uppercase tracking-widest text-[#6B6B6B]">Employee name (print)</label>
        <input
          data-testid="crew-signoff-name-input"
          list="crew-name-suggestions"
          value={empName} onChange={(e) => setEmpName(e.target.value)}
          className="mt-1.5 mb-4 w-full rounded-xl bg-[#0E0E0E] border border-[#2E2E2E] px-4 py-3 outline-none focus:border-[#E11D2A] transition-colors"
        />
        <datalist id="crew-name-suggestions">
          {(suggestions || []).map((n) => <option key={n} value={n} />)}
        </datalist>

        <label className="font-mono text-[10px] uppercase tracking-widest text-[#6B6B6B]">Signature (type your name)</label>
        <input
          data-testid="crew-signoff-signature-input"
          value={signature} onChange={(e) => setSignature(e.target.value)}
          placeholder="Sign here"
          className="mt-1.5 mb-4 w-full rounded-xl bg-[#0E0E0E] border border-[#2E2E2E] px-4 py-3 text-xl italic outline-none focus:border-[#E11D2A] transition-colors"
        />

        <label className="font-mono text-[10px] uppercase tracking-widest text-[#6B6B6B]">Time out</label>
        <div className="mt-1.5 flex gap-2 mb-5">
          <input
            data-testid="crew-signoff-timeout-input"
            value={timeOut} onChange={(e) => setTimeOut(e.target.value)}
            placeholder="10:35 PM"
            className="flex-1 rounded-xl bg-[#0E0E0E] border border-[#2E2E2E] px-4 py-3 outline-none focus:border-[#E11D2A] transition-colors"
          />
          <button onClick={nowTime} className="rounded-xl border border-[#2E2E2E] bg-[#0E0E0E] px-4 flex items-center gap-2 text-sm text-[#A3A3A3] hover:border-[#E11D2A]/50 transition-colors">
            <Clock className="h-4 w-4" /> Now
          </button>
        </div>

        <button
          data-testid="crew-signoff-submit-button"
          onClick={submit} disabled={busy}
          className="w-full rounded-xl bg-[#E11D2A] text-white font-heading font-bold py-3.5 hover:bg-[#B91C1C] active:scale-[0.99] disabled:opacity-60 transition-all"
        >
          {busy ? "Saving…" : "Record Sign-Off"}
        </button>
      </div>

      {signoffs.length > 0 && (
        <div className="rounded-2xl border border-[#2E2E2E] bg-[#171717] p-5">
          <h4 className="font-heading font-semibold mb-3">Recorded sign-offs ({signoffs.length})</h4>
          <div className="space-y-2">
            {signoffs.map((s, i) => (
              <div key={`${s.station}-${s.employee_name}-${s.time_out}-${i}`} className="flex items-center justify-between rounded-lg bg-[#0E0E0E] border border-[#2E2E2E] px-4 py-3">
                <div>
                  <p className="font-medium text-[#F5F5F5]">{s.employee_name}</p>
                  <p className="text-xs text-[#6B6B6B]">{s.station}</p>
                </div>
                <span className="font-mono text-xs text-[#E11D2A]">Out {s.time_out}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
